import { all, takeEvery, select, put, call } from 'typed-redux-saga';
import { PayloadAction } from '@reduxjs/toolkit';

import { RELOAD } from 'src/store/actions/actionTypes';
import {
    updateAssetRemovalFailed,
    updateAssetRemovalSuccess,
    updateBackgroundRemovalFailed,
    updateBackgroundRemovalSuccess,
    uploadBackgrounds,
} from 'src/pages/editor/components/backgrounds/slice';
import { overlayUploaded } from 'src/pages/editor/components/overlays/slice';
import { maskUploaded, textureUploaded } from 'src/pages/editor/components/documents/slice';
import { renderCanvasSuccess, renderCanvasFailed } from 'src/pages/editor/components/canvas/slice';
import {
    publishSceneSuccess,
    publishSceneFailed,
    duplicateSceneSuccess,
    duplicateSceneFailed,
    saveDraftSuccess,
    saveDraftFailed,
    createSceneFailed,
    createBulkScenesFailed,
    saveAndPublishSceneSuccess,
    saveAndPublishSceneFailed,
    createSceneFinished,
} from 'src/pages/editor/components/scene/slice';
import { deleteSceneFailed, restoreSceneFailed } from 'src/components/scenes/slice';
import { loadEditorPage } from 'src/pages/editor/slice';
import {
    retrieveSurfacesSuccess,
    retrieveSurfacesFailed,
    validateEditorSkuSuccess,
    clearEditorSku,
} from 'src/pages/editor/components/surfaces/slice';
import { saveLinkVariationFailed, saveVariationFailed } from 'src/pages/editor/components/SceneVariation/slice';
import { getAllAlerts, getAllAlertsWithName } from './selectors';
import {
    renderFailed,
    publishComplete,
    publishFailed,
    saveComplete,
    saveFailed,
    bulkFailed,
    removeAlert,
    insertVariablesWarning,
    SaveVariantFailed,
    SaveLinkVariantFailed,
    ReplaceBackgroundRemovalFailed,
    ReplaceBackgroundRemovalSuccess,
    deleteSceneFailedAlert,
    restoreSceneFailedAlert,
} from './slice';

// Removing alerts
export function* removeAlertByName(name: string): Generator<any, void, any> {
    const alerts = yield* select(getAllAlertsWithName, name);
    const removedAlerts = alerts.map((alert: any) => put(removeAlert(alert.id)));
    yield* all(removedAlerts);
}

// Adding alerts
function* addCanvasFailed(): Generator<any, void, any> {
    yield* put(renderFailed());
}

function* addPublishSuccess(): Generator<any, void, any> {
    yield* put(publishComplete());
    yield* call(removeAlertByName, 'PUBLISH_FAILED');
}

function* addPublishFailed({ payload }: PayloadAction<string | undefined>): Generator<any, void, any> {
    yield* put(publishFailed(payload));
}

function* addSaveSuccess(): Generator<any, void, any> {
    yield* put(saveComplete());
    yield* call(removeAlertByName, 'SAVE_FAILED');
}

function* addSaveFailed({ payload }: PayloadAction<string | undefined>): Generator<any, void, any> {
    yield* put(saveFailed(payload));
}

function* addBulkFailed({ payload }: PayloadAction<string | undefined>): Generator<any, void, any> {
    yield* put(bulkFailed(payload));
}

function* addVariablesWarning(): Generator<any, void, any> {
    yield* put(insertVariablesWarning());
}

function* addSaveVariantFailed({ payload }: PayloadAction<string | undefined>): Generator<any, void, any> {
    yield* put(SaveVariantFailed(payload));
}

function* addSaveLinkVariantFailed({ payload }: PayloadAction<string | undefined>): Generator<any, void, any> {
    yield* put(SaveLinkVariantFailed(payload));
}

function* addDeleteSceneFailed({
    payload,
}: PayloadAction<{ sceneId: string; apiMessage?: string }>): Generator<any, void, any> {
    yield* put(deleteSceneFailedAlert(payload.apiMessage));
}

function* addRestoreSceneFailed({
    payload,
}: PayloadAction<{ sceneId: string; apiMessage?: string }>): Generator<any, void, any> {
    yield* put(restoreSceneFailedAlert(payload.apiMessage));
}

function* alertRemovalBackgroundFailed(): Generator<any, void, any> {
    yield* put(ReplaceBackgroundRemovalFailed());
}

function* alertRemovalBackgroundSuccess(): Generator<any, void, any> {
    yield* put(ReplaceBackgroundRemovalSuccess());
}

function* clearRelatedFailures({ payload }: PayloadAction<{ name: string }>): Generator<any, any, any> {
    const { name } = payload;
    switch (name) {
        case 'SAVE_COMPLETE':
            yield* call(removeAlertByName, 'SAVE_FAILED');
            break;
        case 'PUBLISH_COMPLETE':
            yield* call(removeAlertByName, 'PUBLISH_FAILED');
            break;
        case 'LINK_SERVER_SAVE_SUCCESS':
            yield* call(removeAlertByName, 'LINK_FAILED');
            break;
        default:
        // none
    }
    return payload;
}

function* clearAllAlertsOtherThanCOAM(): Generator<any, void, any> {
    const alerts = yield* select(getAllAlerts);
    const removedAlerts = alerts
        .filter((alert: any) => alert.name !== 'NO_PERMISSIONS')
        .map((alert: any) => put(removeAlert(alert.id)));
    yield* all(removedAlerts);
}

function* clearCanvasFailure(): Generator<any, void, any> {
    yield* call(removeAlertByName, 'RENDER_FAILED');
}

function* clearUploadFailed(): Generator<any, void, any> {
    yield* call(removeAlertByName, 'UPLOAD_FAILED');
}

function* clearLoadProductFailed(): Generator<any, void, any> {
    yield* call(removeAlertByName, 'LOAD_PRODUCT_FAILED');
}

function* clearLoadSceneFailed(): Generator<any, void, any> {
    yield* call(removeAlertByName, 'LOAD_SCENE_FAILED');
}

function* clearVariabledWarning(): Generator<any, void, any> {
    yield* call(removeAlertByName, 'INSERT_VARIABLES_WARNING');
    yield* call(removeAlertByName, 'PRODUCT_MISSING');
}

export default function* alertsSagas() {
    return yield* all([
        yield* takeEvery(createSceneFinished, addPublishSuccess),
        yield* takeEvery(createSceneFailed, addPublishFailed),
        yield* takeEvery(saveAndPublishSceneSuccess, addPublishSuccess),
        yield* takeEvery(saveAndPublishSceneFailed, addPublishFailed),
        yield* takeEvery(publishSceneSuccess, addPublishSuccess),
        yield* takeEvery(publishSceneFailed, addPublishFailed),
        yield* takeEvery(duplicateSceneSuccess, addSaveSuccess),
        yield* takeEvery(duplicateSceneFailed, addSaveFailed),
        yield* takeEvery(saveDraftSuccess, addSaveSuccess),
        yield* takeEvery(saveDraftFailed, addSaveFailed),
        yield* takeEvery(createBulkScenesFailed, addBulkFailed),
        yield* takeEvery(renderCanvasFailed, addCanvasFailed),
        yield* takeEvery(retrieveSurfacesFailed, addVariablesWarning),
        yield* takeEvery(removeAlert, clearRelatedFailures),
        yield* takeEvery(renderCanvasSuccess, clearCanvasFailure),
        yield* takeEvery(uploadBackgrounds, clearUploadFailed),
        yield* takeEvery(overlayUploaded, clearUploadFailed),
        yield* takeEvery(maskUploaded, clearUploadFailed),
        yield* takeEvery(textureUploaded, clearUploadFailed),
        yield* takeEvery(validateEditorSkuSuccess, clearLoadProductFailed),
        yield* takeEvery(clearEditorSku, clearLoadProductFailed),
        yield* takeEvery(retrieveSurfacesSuccess, clearVariabledWarning),
        yield* takeEvery(RELOAD, clearLoadSceneFailed),
        yield* takeEvery(loadEditorPage, clearAllAlertsOtherThanCOAM),
        yield* takeEvery(saveVariationFailed, addSaveVariantFailed),
        yield* takeEvery(saveLinkVariationFailed, addSaveLinkVariantFailed),
        yield* takeEvery(deleteSceneFailed, addDeleteSceneFailed),
        yield* takeEvery(restoreSceneFailed, addRestoreSceneFailed),
        yield* takeEvery(updateBackgroundRemovalFailed, alertRemovalBackgroundFailed),
        yield* takeEvery(updateBackgroundRemovalSuccess, alertRemovalBackgroundSuccess),
        yield* takeEvery(updateAssetRemovalFailed, alertRemovalBackgroundFailed),
        yield* takeEvery(updateAssetRemovalSuccess, alertRemovalBackgroundSuccess),
    ]);
}
